import { useEffect, useRef, useCallback } from 'react';
import type { PlayerAction } from '../playerReducer';
import type { KeyboardShortcut } from '../types';

export const KEYBOARD_SHORTCUTS: KeyboardShortcut[] = [
  { key: 'Space', description: 'Play / Pause' },
  { key: '←', description: 'Step back one frame' },
  { key: '→', description: 'Step forward one frame' },
  { key: 'Shift + ←', description: 'Step back one second' },
  { key: 'Shift + →', description: 'Step forward one second' },
  { key: '↑', description: 'Previous marker' },
  { key: '↓', description: 'Next marker' },
  { key: 'Home', description: 'Go to beginning' },
  { key: 'End', description: 'Go to end' },
  { key: 'J', description: 'Rewind' },
  { key: 'K', description: 'Pause' },
  { key: 'L', description: 'Fast forward' },
  { key: 'K + J', description: 'Slow step backward (hold)' },
  { key: 'K + L', description: 'Slow step forward (hold)' },
  { key: ',', description: 'Back 5 seconds' },
  { key: '.', description: 'Forward 5 seconds' },
  { key: 'M', description: 'Mute / Unmute' },
  { key: 'Shift + F', description: 'Toggle fullscreen' },
  { key: 'C', description: 'Toggle captions' },
  { key: 'Escape', description: 'Close menus' },
];

interface KeyboardNavOptions {
  mediaEl: HTMLMediaElement | null;
  containerEl: HTMLElement | null;
  dispatch: React.Dispatch<PlayerAction>;
  fps: number;
  onSeek?: (time: number) => void;
  onBeforeSeek?: () => boolean;
  onFullscreenToggle?: () => void;
  onMuteToggle?: () => void;
  onNextMarker?: () => void;
  onPrevMarker?: () => void;
  onShowStatus?: (message: string) => void;
  basePlaybackRateRef?: React.RefObject<number>;
}

export function useKeyboardNav({
  mediaEl,
  containerEl,
  dispatch,
  fps,
  onSeek,
  onBeforeSeek,
  onFullscreenToggle,
  onMuteToggle,
  onNextMarker,
  onPrevMarker,
  onShowStatus,
  basePlaybackRateRef,
}: KeyboardNavOptions) {
  const rewindRateRef = useRef(0);
  const rewindIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isKHeldRef = useRef(false);
  const slowStepIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearRewind = useCallback(() => {
    if (rewindIntervalRef.current) {
      clearInterval(rewindIntervalRef.current);
      rewindIntervalRef.current = null;
    }
    rewindRateRef.current = 0;
  }, []);

  const clearSlowStep = useCallback(() => {
    if (slowStepIntervalRef.current) {
      clearInterval(slowStepIntervalRef.current);
      slowStepIntervalRef.current = null;
    }
  }, []);

  const fakeRewind = useCallback(() => {
    if (!mediaEl) return;

    if (rewindRateRef.current < 2.0) {
      rewindRateRef.current += 0.25;
    }

    if (rewindIntervalRef.current) {
      clearInterval(rewindIntervalRef.current);
    }

    rewindIntervalRef.current = setInterval(() => {
      mediaEl.playbackRate = 1.0;

      if (mediaEl.currentTime <= 0.1) {
        mediaEl.pause();
        clearRewind();
        mediaEl.currentTime = 0;
      } else {
        // Move currentTime back BEFORE calling play() — otherwise
        // play() on an ended video resets to the beginning.
        mediaEl.currentTime -= rewindRateRef.current;
        if (mediaEl.paused) mediaEl.play();
      }
    }, 100);
  }, [mediaEl, clearRewind]);

  const rewind = useCallback(() => {
    if (!mediaEl) return;

    // If fast-forwarding and still playing, ramp the rate down.
    // Skip the ramp-down when ended — the high rate is meaningless
    // because nothing is playing.
    if (mediaEl.playbackRate > 1.0 && !mediaEl.ended) {
      mediaEl.playbackRate -= 2.0;
      if (mediaEl.playbackRate === 0) mediaEl.playbackRate = 1.0;
      onShowStatus?.(`${mediaEl.playbackRate}x`);
      return;
    }

    // Reset a stale high rate (e.g. after fast-forwarding to the end)
    if (mediaEl.playbackRate > 1.0) {
      mediaEl.playbackRate = 1.0;
    }

    // Try native reverse playback (Safari)
    let expectedRate = mediaEl.playbackRate || 1.0;

    try {
      if (expectedRate > -16.0) {
        if (expectedRate > 0 && expectedRate < 2.0) {
          expectedRate = -1.0;
        } else if (expectedRate >= -1.0) {
          expectedRate = -2.0;
        } else {
          expectedRate -= 2.0;
        }
      }

      mediaEl.playbackRate = expectedRate;

      if (expectedRate !== mediaEl.playbackRate) {
        fakeRewind();
        onShowStatus?.(`-${rewindRateRef.current * 4}x`);
      } else {
        mediaEl.play();
        onShowStatus?.(`${expectedRate}x`);
      }
    } catch {
      fakeRewind();
      onShowStatus?.(`-${rewindRateRef.current * 4}x`);
    }
  }, [mediaEl, fakeRewind, onShowStatus]);

  const fastForward = useCallback(() => {
    if (!mediaEl) return;

    // Already at the end — do nothing.
    if (mediaEl.ended || (mediaEl.duration && mediaEl.currentTime >= mediaEl.duration)) return;

    if (rewindRateRef.current > 0) {
      rewindRateRef.current -= 0.5;
      return;
    }

    if (mediaEl.paused) {
      mediaEl.playbackRate = 1.0;
      mediaEl.play();
      onShowStatus?.(`${mediaEl.playbackRate}x`);
      return;
    }

    clearRewind();
    mediaEl.pause();

    if (mediaEl.playbackRate < 16.0) {
      if (mediaEl.playbackRate + 2.0 === 0) {
        mediaEl.playbackRate = 1.0;
      } else if (mediaEl.playbackRate < 2.0) {
        mediaEl.playbackRate += 0.5;
      } else {
        mediaEl.playbackRate += 2.0;
      }
    }

    if (mediaEl.playbackRate === 0) mediaEl.playbackRate = 1.0;
    mediaEl.play();
    onShowStatus?.(`${mediaEl.playbackRate}x`);
  }, [mediaEl, clearRewind, onShowStatus]);

  const incrementFrames = useCallback(
    (num: number) => {
      if (!mediaEl) return;
      mediaEl.pause();
      if (fps > 0) {
        // Convert to integer frame number, increment, convert back.
        // This avoids floating-point drift from repeated 1/fps additions.
        const currentFrame = Math.round(mediaEl.currentTime * fps);
        const targetFrame = currentFrame + num;
        mediaEl.currentTime = Math.max(0, targetFrame / fps);
      } else {
        mediaEl.currentTime = Math.max(0, mediaEl.currentTime + num);
      }
      onSeek?.(mediaEl.currentTime);
    },
    [mediaEl, fps, onSeek],
  );

  const startSlowStep = useCallback(
    (direction: number) => {
      clearSlowStep();
      incrementFrames(direction); // immediate first step
      slowStepIntervalRef.current = setInterval(() => {
        incrementFrames(direction);
      }, 200); // ~5 fps shuttle speed
    },
    [clearSlowStep, incrementFrames],
  );

  const togglePlayback = useCallback(() => {
    if (!mediaEl) return;
    clearRewind();
    if (mediaEl.paused) {
      if (onBeforeSeek && !onBeforeSeek()) return;
      mediaEl.playbackRate = basePlaybackRateRef?.current ?? 1.0;
      mediaEl.play();
    } else {
      mediaEl.pause();
    }
  }, [mediaEl, clearRewind, basePlaybackRateRef, onBeforeSeek]);

  useEffect(() => {
    if (!containerEl || !mediaEl) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture keys when typing in input fields.
      // Use composedPath()[0] to see through Shadow DOM boundaries (e.g. <comment-textarea>).
      const target = e.target as HTMLElement;
      const actualTarget = (e.composedPath()[0] ?? target) as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        actualTarget.isContentEditable
      ) {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlayback();
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (onBeforeSeek && !onBeforeSeek()) break;
          clearRewind();
          if (e.shiftKey) {
            mediaEl.currentTime -= 1.0;
          } else {
            incrementFrames(-1);
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          if (onBeforeSeek && !onBeforeSeek()) break;
          clearRewind();
          if (e.shiftKey) {
            mediaEl.currentTime += 1.0;
          } else {
            incrementFrames(1);
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          onPrevMarker?.();
          break;

        case 'ArrowDown':
          e.preventDefault();
          onNextMarker?.();
          break;

        case 'j':
        case 'J':
          if (e.repeat) break;
          if (isKHeldRef.current) {
            if (!slowStepIntervalRef.current) startSlowStep(-1);
          } else {
            rewind();
          }
          break;

        case 'k':
        case 'K':
          if (!isKHeldRef.current) {
            isKHeldRef.current = true;
            clearRewind();
            clearSlowStep();
            mediaEl.playbackRate = 1.0;
            mediaEl.pause();
          }
          break;

        case 'l':
        case 'L':
          if (e.repeat) break;
          if (isKHeldRef.current) {
            if (!slowStepIntervalRef.current) startSlowStep(1);
          } else {
            fastForward();
          }
          break;

        case 'Home':
          e.preventDefault();
          if (onBeforeSeek && !onBeforeSeek()) break;
          clearRewind();
          mediaEl.currentTime = 0;
          break;

        case 'End':
          e.preventDefault();
          if (onBeforeSeek && !onBeforeSeek()) break;
          clearRewind();
          mediaEl.currentTime = mediaEl.duration;
          break;

        case 'f':
        case 'F':
          if (e.shiftKey) {
            onFullscreenToggle?.();
          }
          break;

        case ',':
          e.preventDefault();
          if (onBeforeSeek && !onBeforeSeek()) break;
          clearRewind();
          mediaEl.currentTime = Math.max(0, mediaEl.currentTime - 5.0);
          onSeek?.(mediaEl.currentTime);
          break;

        case '.':
          e.preventDefault();
          if (onBeforeSeek && !onBeforeSeek()) break;
          clearRewind();
          mediaEl.currentTime = Math.min(mediaEl.duration || 0, mediaEl.currentTime + 5.0);
          onSeek?.(mediaEl.currentTime);
          break;

        case 'm':
        case 'M':
          onMuteToggle?.();
          break;

        case 'c':
        case 'C':
          dispatch({ type: 'TOGGLE_CAPTION_PICKER' });
          break;

        case 'Escape':
          dispatch({ type: 'CLOSE_ALL_MENUS' });
          break;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key === 'k') {
        isKHeldRef.current = false;
        clearSlowStep();
      }
      if (key === 'j' || key === 'l') {
        clearSlowStep();
      }
    };

    containerEl.addEventListener('keydown', handleKeyDown);
    containerEl.addEventListener('keyup', handleKeyUp);
    return () => {
      containerEl.removeEventListener('keydown', handleKeyDown);
      containerEl.removeEventListener('keyup', handleKeyUp);
      clearRewind();
      clearSlowStep();
    };
  }, [
    containerEl,
    mediaEl,
    dispatch,
    togglePlayback,
    clearRewind,
    clearSlowStep,
    rewind,
    fastForward,
    incrementFrames,
    startSlowStep,
    onFullscreenToggle,
    onMuteToggle,
    onNextMarker,
    onPrevMarker,
    onBeforeSeek,
    onShowStatus,
    basePlaybackRateRef,
  ]);
}
